import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
    PawPrint, Plus, ShieldCheck, ArrowUpRight, ArrowDown, ArrowRight,
    Bell, LockKey, IdentificationCard, Sparkle, MapPin, Crown, QrCode,
    Heart, Siren, MapTrifold,
} from "@phosphor-icons/react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import InstallSection from "@/components/InstallSection";
import ThankYouModal from "@/components/ThankYouModal";
import DowngradedBanner from "@/components/DowngradedBanner";
import ProtectionBanner from "@/components/ProtectionBanner";
import EarlyAccessWelcome from "@/components/EarlyAccessWelcome";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useIsStoreApp } from "@/lib/storeAppDetection";

/* Cinematic photography (Unsplash) — hero + two emotional scenes only */
const IMG_HERO = "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=2000&q=80";
const IMG_SCAN = "https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&w=1400&q=80";

const easeOut = [0.16, 1, 0.3, 1];

export default function Home() {
    const { user } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [pets, setPets] = useState([]);

    useEffect(() => {
        // Only fetch pets when the visitor is authenticated. On the public marketing landing
        // (getpawlink.com) this endpoint would 401 for anonymous visitors — skip it.
        if (!user) return;
        (async () => {
            try { const { data } = await api.get("/pets"); setPets(data); } catch { /* ignore */ }
        })();
    }, [user]);

    const firstPetName = pets[0]?.name || "Milo";
    const isPremium = user?.plan === "premium";
    const isStoreApp = useIsStoreApp();

    return (
        <div
            className="min-h-screen bg-paw-bone dark:bg-paw-night text-paw-ink dark:text-white overflow-x-clip"
            data-testid="home-page"
        >
            <Nav />
            <ThankYouModal />
            <main className="pb-24">
                <EarlyAccessWelcome />
                <ProtectionBanner />
                <DowngradedBanner />
                <CinematicHero t={t} onProtect={() => nav(user ? "/pets/new" : "/register")} isSignedIn={Boolean(user)} />

                {/* Install PawLink — website-only. Component self-hides in standalone (installed) mode. */}
                <InstallSection t={t} />

                <StorySection
                    testId="home-scene-scan"
                    kicker={t("home_story.s1_kicker")}
                    heading={t("home_story.s1_h")}
                    body={t("home_story.s1_sub")}
                    image={IMG_SCAN}
                    tint="emerald"
                    overlay={<ScanOverlay t={t} firstPetName={firstPetName} />}
                />

                <BenefitsGrid t={t} />

                {user && (
                    <section
                        data-testid="home-family"
                        className="relative mt-20 sm:mt-28 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
                    >
                        <FamilySummary t={t} pets={pets} onManage={() => nav("/pets")} onAdd={() => nav("/pets/new")} />
                    </section>
                )}

                {/* Google Play compliance: hide the "Protection" upgrade
                    callout for non-Premium users inside the store-app
                    build (no purchase surface). Existing Premium users
                    already see ProtectionBanner at the top of the page. */}
                {!(isStoreApp && !isPremium) && (
                    <ProtectionCallout t={t} onLearnMore={() => nav(user ? "/subscription" : "/register")} />
                )}

                <FAQPreview t={t} onSeeAll={() => nav("/faq")} />

                {/* Same rule for the FinalCTA: Premium users keep their
                    thank-you variant; non-Premium store-app users see no
                    upgrade CTA at all. */}
                {!(isStoreApp && !isPremium) && (
                    <FinalCTA t={t} isPremium={isPremium} onUpgrade={() => nav(user ? "/subscription" : "/register")} isSignedIn={Boolean(user)} />
                )}
            </main>
            <Footer />
        </div>
    );
}

/* =====================================================================
 *  CINEMATIC HERO
 * ===================================================================== */
function CinematicHero({ t, onProtect, isSignedIn = true }) {
    const reduce = useReducedMotion();
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 140]);
    const scale = useTransform(scrollY, [0, 600], [1, reduce ? 1 : 1.08]);
    // "Protect my best friend" for every visitor — the emotional first-person CTA
    // outperforms generic "Get started — it's free" for a premium pet-safety brand.
    // Uses `hero_cta_primary` which is translated in EN/AR/FR/ES.
    const ctaLabel = t("home_story.hero_cta_primary");

    return (
        <section
            data-testid="home-hero"
            // min-h (not fixed h) lets the hero grow when content needs more room
            // — critical for Arabic which stacks into 4 taller lines vs 2 in English.
            // No max-h so nothing ever gets clipped.
            className="relative w-full min-h-[92vh] overflow-hidden"
        >
            <motion.div aria-hidden style={{ y, scale }} className="absolute inset-0 will-change-transform">
                <img
                    src={IMG_HERO}
                    alt=""
                    // object-position 50% 35% keeps both pets' faces above the
                    // bottom gradient overlay across every aspect ratio; the
                    // default 50%/50% cuts the top off the corgi at portrait.
                    className="absolute inset-0 w-full h-full object-cover object-[50%_35%]"
                    loading="eager"
                    fetchPriority="high"
                />
            </motion.div>

            <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/85" />
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(120%_60%_at_50%_20%,transparent,rgba(0,0,0,0.55))]" />
            <div aria-hidden className="absolute inset-0 bg-[radial-gradient(60%_40%_at_50%_100%,rgba(30,58,47,0.55),transparent)]" />

            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.9'/%3E%3C/svg%3E\")",
                }}
            />

            <div className="relative z-10 min-h-[92vh] flex flex-col justify-end lg:justify-center pb-16 sm:pb-24 lg:py-24 px-5 sm:px-10 lg:px-16">
                <div className="max-w-3xl">
                    {/* Eyebrow — very subtle first breath */}
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-3.5 py-1.5 text-[10.5px] uppercase tracking-[0.24em] font-semibold text-white/95"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-paw-terra paw-pulse" aria-hidden />
                        {t("home_story.hero_eyebrow")}
                    </motion.p>

                    {/*
                      Cinematic headline — three sequential reveals, no motion,
                      no bouncing, no typing. Just fade + gentle 12–20px upward
                      drift. Easing is Apple-style `cubic-bezier(0.22,1,0.36,1)`.
                    */}
                    <h1
                        className="mt-7 pb-1 font-display text-white text-[clamp(2.5rem,6vw,5rem)] leading-[1.06] rtl:leading-[1.35] tracking-[-0.02em]"
                        aria-label={`${t("home_story.hero_h_a")} ${t("home_story.hero_h_a2")} ${t("home_story.hero_h_b")}`}
                    >
                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
                            className="block text-white/85"
                        >
                            {t("home_story.hero_h_a")}
                        </motion.span>

                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 1.9 }}
                            className="block text-white/90 mt-1"
                        >
                            {t("home_story.hero_h_a2")}
                        </motion.span>

                        {/* Highlight — subtle scale + fade, arrives last after a beat */}
                        <motion.span
                            initial={{ opacity: 0, y: 16, scale: 0.985 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 3.05 }}
                            className="block mt-2"
                        >
                            <span className="inline-block pb-2 bg-gradient-to-r from-paw-terra via-amber-300 to-paw-terra bg-clip-text text-transparent">
                                {t("home_story.hero_h_b")}
                            </span>
                        </motion.span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 4.0 }}
                        className="mt-7 max-w-xl text-white/80 text-base sm:text-lg leading-relaxed"
                    >
                        {t("home_story.hero_sub")}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 4.6 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            type="button"
                            onClick={onProtect}
                            data-testid="home-hero-cta-primary"
                            className="group inline-flex items-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark px-7 py-4 text-white font-semibold shadow-hover transition-all duration-300 hover:-translate-y-0.5"
                        >
                            <Heart size={16} weight="fill" />
                            {ctaLabel}
                            <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        <div
                            className="inline-flex items-center gap-2 text-white/70 text-xs tracking-wide"
                            dir="ltr"
                        >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 paw-pulse" />
                            <span>{t("home_story.pill_scan")}</span>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* =====================================================================
 *  STORY SECTION — reusable split cinematic scene (scan + lost only)
 * ===================================================================== */
function StorySection({ testId, kicker, heading, body, image, tint, overlay, reverse = false }) {
    const reduce = useReducedMotion();

    const tintMap = {
        emerald: { grad: "from-emerald-500/25 via-emerald-500/5 to-transparent", dot: "bg-emerald-400", text: "text-emerald-400 dark:text-emerald-300" },
        rose:    { grad: "from-rose-600/40 via-red-500/10 to-transparent",     dot: "bg-rose-500",     text: "text-rose-500 dark:text-rose-300"     },
    };
    const c = tintMap[tint] || tintMap.emerald;

    return (
        <section data-testid={testId} className="relative py-14 sm:py-24 lg:py-28">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className={`grid gap-10 lg:gap-16 lg:grid-cols-2 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                    <motion.div
                        initial={{ opacity: 0, y: reduce ? 0 : 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, ease: easeOut }}
                        className="relative aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] rounded-[28px] overflow-hidden shadow-hover ring-1 ring-black/5 dark:ring-white/10"
                    >
                        <img src={image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        <div aria-hidden className={`absolute inset-0 bg-gradient-to-t ${c.grad} mix-blend-multiply`} />
                        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/15" />
                        {overlay}
                    </motion.div>

                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: reduce ? 0 : 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.7, ease: easeOut, delay: 0.05 }}
                            className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${c.text}`}
                            dir="ltr"
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${c.dot} paw-pulse`} aria-hidden />
                            {kicker}
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.9, ease: easeOut, delay: 0.15 }}
                            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-paw-ink dark:text-paw-bone"
                        >
                            {heading}
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: reduce ? 0 : 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.9, ease: easeOut, delay: 0.25 }}
                            className="mt-5 text-base sm:text-lg leading-relaxed text-paw-ink2 dark:text-paw-bone/70 max-w-xl"
                        >
                            {body}
                        </motion.p>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------- Scene overlays ---------- */
function ScanOverlay({ t, firstPetName }) {
    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 0.7, ease: easeOut, delay: 0.35 }}
                className="absolute top-5 start-5 end-5 sm:end-auto sm:max-w-[280px] rounded-2xl bg-white/95 dark:bg-white/95 backdrop-blur shadow-hover p-3 flex items-center gap-3"
                dir="ltr"
            >
                <div className="w-10 h-10 rounded-xl bg-emerald-500 grid place-items-center flex-shrink-0">
                    <Bell size={18} weight="fill" className="text-white" />
                </div>
                <div className="min-w-0 text-left">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-600">PawLink</p>
                    <p className="text-sm font-semibold text-paw-ink truncate">{t("home_story.s1_note_title")}</p>
                    <p className="text-[11px] text-paw-ink2 truncate">{t("home_story.s1_note_body")}</p>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 0.8, ease: easeOut, delay: 0.5 }}
                className="absolute bottom-5 start-5 inline-flex items-center gap-2 rounded-full bg-black/45 backdrop-blur px-3 py-1.5 border border-white/15"
                dir="ltr"
            >
                <QrCode size={14} weight="fill" className="text-white" />
                <span className="text-[11px] text-white font-medium tracking-wide">Smart QR · {firstPetName}</span>
            </motion.div>
        </>
    );
}

/* LostOverlay removed with Lost Pets feature (temporarily hidden pre-launch). */

/* =====================================================================
 *  BENEFITS — ONE premium card, three emotional pillars.
 *  Replaces the previous 6-tile grid. Every word chosen to make the
 *  visitor feel: "PawLink could help bring my pet home."
 * ===================================================================== */
function BenefitsGrid({ t }) {
    const reduce = useReducedMotion();

    const pillars = [
        {
            Icon: IdentificationCard,
            title: t("home_story.big_b1_h"),
            sub: t("home_story.big_b1_sub"),
        },
        {
            Icon: Siren,
            title: t("home_story.big_b2_h"),
            sub: t("home_story.big_b2_sub"),
        },
        {
            Icon: QrCode,
            title: t("home_story.big_b3_h"),
            sub: t("home_story.big_b3_sub"),
        },
        {
            Icon: MapTrifold,
            title: t("home_story.big_b4_h"),
            sub: t("home_story.big_b4_sub"),
        },
    ];

    return (
        <section data-testid="home-benefits" className="relative py-14 sm:py-24 lg:py-28">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Kicker + heading */}
                <div className="text-center max-w-2xl mx-auto">
                    <motion.p
                        initial={{ opacity: 0, y: reduce ? 0 : 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.6, ease: easeOut }}
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-forest dark:text-paw-terra"
                        dir="ltr"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-paw-terra paw-pulse" aria-hidden />
                        {t("home_story.benefits_kicker")}
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: reduce ? 0 : 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.8, ease: easeOut, delay: 0.1 }}
                        className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-paw-ink dark:text-paw-bone"
                    >
                        {t("home_story.benefits_h")}
                    </motion.h2>
                </div>

                {/* ONE premium card containing 3 emotional pillars */}
                <motion.div
                    initial={{ opacity: 0, y: reduce ? 0 : 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.9, ease: easeOut, delay: 0.2 }}
                    data-testid="home-benefits-card"
                    className="relative mt-12 sm:mt-16 overflow-hidden rounded-[32px] border border-paw-line dark:border-white/10 bg-white/70 dark:bg-[hsl(152,15%,8%)] backdrop-blur-xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]"
                >
                    {/* Ambient orange glow — very restrained */}
                    <div aria-hidden className="pointer-events-none absolute -top-24 -end-24 w-72 h-72 rounded-full bg-paw-terra/12 blur-3xl" />
                    <div aria-hidden className="pointer-events-none absolute -bottom-24 -start-24 w-72 h-72 rounded-full bg-emerald-500/8 blur-3xl" />

                    <div className="relative divide-y divide-paw-line/70 dark:divide-white/[0.06]">
                        {pillars.map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: reduce ? 0 : 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-5%" }}
                                transition={{ duration: 0.7, ease: easeOut, delay: 0.3 + i * 0.15 }}
                                data-testid={`home-benefit-pillar-${i + 1}`}
                                className="flex gap-5 sm:gap-7 p-6 sm:p-9 lg:p-11"
                            >
                                {/* Large numbered indicator + subtle icon */}
                                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                                    <span
                                        aria-hidden
                                        className="font-display text-4xl sm:text-5xl leading-none tracking-tight bg-gradient-to-b from-paw-terra to-amber-500 bg-clip-text text-transparent"
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="w-11 h-11 rounded-xl bg-paw-terra/10 dark:bg-paw-terra/15 ring-1 ring-paw-terra/25 grid place-items-center text-paw-terra">
                                        <p.Icon size={20} weight="fill" />
                                    </span>
                                </div>

                                <div className="min-w-0">
                                    <h3 className="font-display text-xl sm:text-2xl leading-tight tracking-tight text-paw-ink dark:text-paw-bone">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-paw-ink2 dark:text-paw-bone/70">
                                        {p.sub}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Emotional closing — reassures the visitor after the four promises */}
                <motion.p
                    initial={{ opacity: 0, y: reduce ? 0 : 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.9, ease: easeOut, delay: 0.9 }}
                    data-testid="home-benefits-close"
                    className="mt-10 sm:mt-12 text-center font-display italic text-lg sm:text-xl text-paw-ink2 dark:text-paw-bone/70"
                >
                    <Heart size={16} weight="fill" className="inline-block me-2 text-paw-terra" aria-hidden />
                    {t("home_story.benefits_close")}
                </motion.p>
            </div>
        </section>
    );
}

/* =====================================================================
 *  FAMILY SUMMARY — bridge to /pets management + community feeling
 * ===================================================================== */
function FamilySummary({ t, pets, onManage, onAdd }) {
    const reduce = useReducedMotion();
    const count = pets.length;
    const heading =
        count === 0
            ? t("home_story.family_h_none")
            : count === 1
                ? t("home_story.family_h_one")
                : t("home_story.family_h_many", { count });

    return (
        <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.8, ease: easeOut }}
            className="relative overflow-hidden rounded-[28px] border border-paw-line dark:border-white/10 bg-white dark:bg-[hsl(152,12%,10%)] p-6 sm:p-10"
        >
            <div aria-hidden className="absolute -top-24 -end-24 w-64 h-64 rounded-full bg-paw-terra/10 blur-3xl" />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-forest dark:text-paw-terra">
                        {t("home_story.family_kicker")}
                    </p>
                    <h2
                        data-testid="home-family-heading"
                        className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-paw-ink dark:text-paw-bone"
                    >
                        {heading}
                    </h2>
                    <p className="mt-2 text-sm sm:text-base text-paw-ink2 dark:text-paw-bone/60">
                        {t("home_story.family_sub")}
                    </p>

                    {count > 0 && (
                        <div className="mt-5 flex items-center" data-testid="home-family-avatars">
                            {pets.slice(0, 5).map((p, i) => (
                                <div
                                    key={p.id}
                                    className="relative w-11 h-11 rounded-full ring-4 ring-white dark:ring-[hsl(152,12%,10%)] overflow-hidden bg-paw-muted dark:bg-white/5"
                                    style={{ marginInlineStart: i === 0 ? 0 : -12, zIndex: 10 - i }}
                                    title={p.name}
                                >
                                    {p.photo_path ? (
                                        <img src={fileUrl(p.photo_path)} alt={p.name} className="pet-photo absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                    ) : (
                                        <div className="absolute inset-0 grid place-items-center text-paw-forest dark:text-paw-terra">
                                            <PawPrint size={18} weight="fill" />
                                        </div>
                                    )}
                                </div>
                            ))}
                            {count > 5 && (
                                <div
                                    className="relative w-11 h-11 rounded-full ring-4 ring-white dark:ring-[hsl(152,12%,10%)] bg-paw-forest dark:bg-paw-terra text-white grid place-items-center text-xs font-semibold"
                                    style={{ marginInlineStart: -12 }}
                                >
                                    +{count - 5}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 md:flex-shrink-0">
                    {count > 0 ? (
                        <>
                            <button
                                type="button"
                                onClick={onManage}
                                data-testid="home-manage-pets"
                                className="inline-flex items-center gap-2 rounded-full bg-paw-forest hover:bg-paw-forestDark dark:bg-paw-terra dark:hover:bg-paw-terraDark px-6 py-3 text-white text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-soft hover:shadow-hover justify-center"
                            >
                                <PawPrint size={16} weight="fill" />
                                {t("home_story.family_cta_manage")}
                                <ArrowUpRight size={14} weight="bold" />
                            </button>
                            <button
                                type="button"
                                onClick={onAdd}
                                data-testid="home-family-add"
                                className="inline-flex items-center gap-2 rounded-full border border-paw-line dark:border-white/15 bg-transparent hover:bg-paw-muted dark:hover:bg-white/5 px-6 py-3 text-paw-ink dark:text-paw-bone text-sm font-semibold transition justify-center"
                            >
                                <Plus size={14} weight="bold" />
                                {t("home_story.family_cta")}
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={onAdd}
                            data-testid="home-family-create"
                            className="inline-flex items-center gap-2 rounded-full bg-paw-forest hover:bg-paw-forestDark dark:bg-paw-terra dark:hover:bg-paw-terraDark px-7 py-3.5 text-white font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-hover"
                        >
                            <Sparkle size={16} weight="fill" />
                            {t("home_story.family_cta_first")}
                            <ArrowUpRight size={16} weight="bold" />
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

/* =====================================================================
 *  PROTECTION CALLOUT — single emotional CTA card that routes to /subscription
 * ===================================================================== */
function ProtectionCallout({ t, onLearnMore }) {
    const reduce = useReducedMotion();
    return (
        <section
            data-testid="home-protection-callout"
            className="relative py-16 sm:py-20 bg-paw-muted/40 dark:bg-white/[0.02]"
        >
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.p
                    initial={{ opacity: 0, y: reduce ? 0 : 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.6, ease: easeOut }}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-forest dark:text-paw-terra"
                    dir="ltr"
                >
                    <Heart size={12} weight="fill" />
                    {t("home_protection.eyebrow")}
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: reduce ? 0 : 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.7, ease: easeOut, delay: 0.1 }}
                    className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-paw-ink dark:text-paw-bone"
                >
                    {t("home_protection.heading")}
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="mt-4 text-paw-ink2 dark:text-paw-bone/70 leading-relaxed max-w-xl mx-auto"
                >
                    {t("home_protection.body")}
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: reduce ? 0 : 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55, delay: 0.3 }}
                    className="mt-7"
                >
                    <button
                        type="button"
                        data-testid="home-protection-cta"
                        onClick={onLearnMore}
                        className="inline-flex items-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-6 py-3 sm:py-3.5 text-sm sm:text-base font-semibold shadow-lg shadow-paw-terra/20 transition-all duration-300 hover:-translate-y-0.5"
                    >
                        <ShieldCheck size={16} weight="fill" />
                        {t("home_protection.cta")}
                        <ArrowRight size={14} weight="bold" className="rtl:rotate-180" />
                    </button>
                </motion.div>
            </div>
        </section>
    );
}

/* =====================================================================
 *  FINAL CTA
 * ===================================================================== */
function FinalCTA({ t, isPremium, onUpgrade, isSignedIn = true }) {
    const reduce = useReducedMotion();
    // For anonymous visitors: never show the "premium active" branch — always show a punchy
    // "Get started for free" CTA that lands them on /register.
    const showPremiumBadge = isSignedIn && isPremium;
    const ctaLabel = isSignedIn
        ? t("home_story.final_cta_upgrade")
        : t("home_pricing.hero_get_started");
    return (
        <section
            data-testid="home-final-cta"
            className="mt-16 sm:mt-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        >
            <motion.div
                initial={{ opacity: 0, y: reduce ? 0 : 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 0.9, ease: easeOut }}
                className="relative overflow-hidden rounded-[32px] bg-paw-forest dark:bg-paw-forestDark text-white p-8 sm:p-14"
            >
                <div aria-hidden className="absolute -top-24 -end-24 w-80 h-80 rounded-full bg-paw-terra/30 blur-3xl" />
                <div aria-hidden className="absolute -bottom-24 -start-24 w-80 h-80 rounded-full bg-emerald-400/15 blur-3xl" />

                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="max-w-xl">
                        <p className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-semibold text-paw-terra">
                            <Crown size={12} weight="fill" />
                            {t("home_story.final_kicker")}
                        </p>
                        <h3 className="mt-4 font-display text-3xl sm:text-4xl leading-tight tracking-tight">
                            {t("home_story.final_h")}
                        </h3>
                        <p className="mt-3 text-white/85 text-sm sm:text-base leading-relaxed">
                            {t("home_story.final_sub")}
                        </p>
                    </div>

                    {showPremiumBadge ? (
                        <div className="flex flex-col items-start md:items-end gap-3">
                            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-400/40 px-4 py-2 text-emerald-200 text-sm font-semibold">
                                <ShieldCheck size={16} weight="fill" /> {t("home_story.final_active")}
                            </span>
                            <button
                                type="button"
                                onClick={onUpgrade}
                                data-testid="home-final-manage"
                                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-semibold hover:bg-white/15 transition"
                            >
                                {t("home_story.final_cta_manage")}
                                <ArrowUpRight size={14} weight="bold" />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={onUpgrade}
                            data-testid="home-final-upgrade"
                            className="inline-flex items-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark px-7 py-4 text-white font-semibold shadow-hover transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
                        >
                            <PawPrint size={18} weight="fill" />
                            {ctaLabel}
                            <ArrowUpRight size={16} weight="bold" />
                        </button>
                    )}
                </div>
            </motion.div>
        </section>
    );
}

/* =====================================================================
 *  FAQ PREVIEW — 4 top questions + link to full /faq
 * ===================================================================== */
function FAQPreview({ t, onSeeAll }) {
    const reduce = useReducedMotion();
    const [openIdx, setOpenIdx] = useState(0);
    const items = [
        { q: t("home_faq.q1_q"), a: t("home_faq.q1_a") },
        { q: t("home_faq.q2_q"), a: t("home_faq.q2_a") },
        { q: t("home_faq.q3_q"), a: t("home_faq.q3_a") },
        { q: t("home_faq.q4_q"), a: t("home_faq.q4_a") },
    ];
    return (
        <section data-testid="home-faq" className="relative py-14 sm:py-24 lg:py-28">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <motion.p
                        initial={{ opacity: 0, y: reduce ? 0 : 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.6, ease: easeOut }}
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-forest dark:text-paw-terra"
                        dir="ltr"
                    >
                        {t("home_faq.eyebrow")}
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: reduce ? 0 : 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.8, ease: easeOut, delay: 0.1 }}
                        className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-paw-ink dark:text-paw-bone"
                    >
                        {t("home_faq.heading")}
                    </motion.h2>
                </div>

                <ul className="mt-10 space-y-3">
                    {items.map((it, i) => {
                        const open = openIdx === i;
                        return (
                            <li
                                key={it.q}
                                className={`rounded-2xl border transition ${
                                    open
                                        ? "border-paw-forest/40 dark:border-paw-terra/40 bg-white dark:bg-white/[0.04] shadow-soft"
                                        : "border-paw-line dark:border-white/10 bg-white/70 dark:bg-white/[0.02] hover:border-paw-forest/30 dark:hover:border-paw-terra/30"
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenIdx(open ? -1 : i)}
                                    data-testid={`home-faq-q-${i + 1}`}
                                    aria-expanded={open}
                                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-start"
                                >
                                    <span className="font-semibold text-paw-ink dark:text-paw-bone text-sm sm:text-base leading-tight">
                                        {it.q}
                                    </span>
                                    <ArrowDown
                                        size={14}
                                        weight="bold"
                                        className={`flex-shrink-0 text-paw-ink2 dark:text-paw-bone/60 transition-transform ${open ? "rotate-180" : ""}`}
                                    />
                                </button>
                                {open && (
                                    <div className="px-5 pb-4 text-sm text-paw-ink2 dark:text-paw-bone/75 leading-relaxed">
                                        {it.a}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>

                <div className="mt-8 text-center">
                    <button
                        type="button"
                        onClick={onSeeAll}
                        data-testid="home-faq-see-all"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-paw-forest dark:text-paw-terra hover:underline underline-offset-4"
                    >
                        {t("home_faq.see_all")}
                        <ArrowUpRight size={12} weight="bold" />
                    </button>
                </div>
            </div>
        </section>
    );
}
